import { useContext, useEffect, useRef } from 'react';
import { useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import { FormContext } from '@stormwater-software/lib-form';
import { InspectionContext } from '../../../contexts/InspectionContext';
import {
  Annotation,
  markupOf,
  saveMarkup,
} from '../../../utils/api/findingMarkup';

type Upload = {
  id?: number;
  file?: unknown;
  deleted?: boolean;
  annotations?: Annotation[];
};

/** the form fields that hold a finding's photos */
const PHOTO_FIELDS = ['images', 'completedImages'] as const;

/** a photo already saved on the server, so its markup can be saved on its own */
const isSavedPhoto = (upload: Upload) =>
  typeof upload.id === 'number' && !upload.file && !upload.deleted;

const withAnnotations = (
  uploads: any[] | undefined,
  uploadId: number,
  annotations: Annotation[],
) =>
  Array.isArray(uploads)
    ? uploads.map((upload) =>
        upload.id === uploadId ? { ...upload, annotations } : upload,
      )
    : uploads;

/**
 * Saves a saved photo's markup as soon as the annotation editor's Save puts it in the finding
 * form, so it no longer waits for the finding itself to be saved (and is no longer lost when the
 * user leaves without saving the finding).
 *
 * The saved rows, with their ids, are written back into the form: a later finding save sends
 * them as existing marks, which the server leaves alone, so they are not added twice. If the save
 * fails (offline, a server error), the markup stays in the form and is saved with the finding,
 * as before. Photos not uploaded yet always save with the finding: there is no upload to save
 * markup against until there is.
 *
 * Saves of one photo are queued (see `saveMarkup`), and only the answer to its newest edit is
 * written back: an older one would put back marks the user has since changed. A finding save
 * waits for the queue, so it can't add the marks a second time. `onSaved` tells a caller that
 * holds its own copy of the finding (the map editor) about the saved rows.
 */
export function useSaveMarkupOnEdit({
  enabled,
  onSaved,
}: {
  enabled: boolean;
  onSaved?: (findingId: number, uploadId: number, rows: Annotation[]) => void;
}) {
  const formContext = useContext(FormContext);
  const inspectionContext = useContext(InspectionContext);
  const queryClient = useQueryClient();
  // photo id → the markup last sent (or loaded with), so only a new edit is sent
  const savedMarkup = useRef(new Map<number, string>());
  // a save resolves later: write into the form as it is then, not as it was when sent
  const latestForm = useRef(formContext);
  latestForm.current = formContext;
  const latestOnSaved = useRef(onSaved);
  latestOnSaved.current = onSaved;

  const findingId: number | undefined = formContext.value.id;
  const inspectionId: number | undefined = inspectionContext?.inspection?.id;

  useEffect(() => {
    if (!enabled || !findingId) return;

    PHOTO_FIELDS.forEach((field) => {
      const uploads: Upload[] | undefined = formContext.value[field];

      if (!Array.isArray(uploads)) return;

      uploads.filter(isSavedPhoto).forEach((upload) => {
        const uploadId = upload.id as number;
        const markup = markupOf(upload.annotations);

        // first sight of this photo: what it loaded with is what the server has
        if (!savedMarkup.current.has(uploadId)) {
          savedMarkup.current.set(uploadId, markup);

          return;
        }

        if (savedMarkup.current.get(uploadId) === markup) return;

        // mark it as sent, so another render can't send it twice while this is in flight
        savedMarkup.current.set(uploadId, markup);

        saveMarkup(findingId, uploadId, upload.annotations || [])
          .then((saved) => {
            // a newer edit of this photo is queued behind this one: its answer is the one to keep
            if (savedMarkup.current.get(uploadId) !== markup) return;

            // the form may have moved on while this was in flight: patch only this photo
            const form = latestForm.current;

            form.replace(field, withAnnotations(form.value[field], uploadId, saved));

            // the Findings tab builds its cards from the inspection's findings and this list
            const patchFinding = (finding: any) =>
              finding.id === findingId
                ? {
                    ...finding,
                    finding_uploads: withAnnotations(
                      finding.finding_uploads,
                      uploadId,
                      saved,
                    ),
                  }
                : finding;

            inspectionContext?.setFindings?.((findings: any[]) =>
              Array.isArray(findings) ? findings.map(patchFinding) : findings,
            );

            if (inspectionId) {
              queryClient.setQueryData(
                ['inspectionFindings', inspectionId],
                (findings: any) =>
                  Array.isArray(findings) ? findings.map(patchFinding) : findings,
              );
            }

            latestOnSaved.current?.(findingId, uploadId, saved);
            toast.success('Markup saved');
          })
          .catch((err: any) => {
            // a newer edit of this photo is queued behind this one and speaks for it
            if (savedMarkup.current.get(uploadId) !== markup) return;

            // refused, not failed: the finding's close is pending review and only its requester
            // may change its photos. A finding save would be refused too, so don't promise one.
            if (err?.response?.status === 409) {
              toast.error(
                `Markup not saved. ${
                  err.response.data?.message || "This finding can't be edited right now."
                }`,
              );

              return;
            }

            // left to the finding save. The attempt stays recorded, so the next edit is sent:
            // forgetting it would take that edit for the markup the photo loaded with.
            toast.error('Markup not saved yet. It will be saved with the finding.');
          });
      });
    });
    // the photo fields are what change on an editor Save; the rest is read when they do
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, findingId, formContext.value.images, formContext.value.completedImages]);
}

export default useSaveMarkupOnEdit;
